/**
 * QA Panel — Campaigns controller (Tournaments sub-tab).
 *
 * Lets QA take over the tournaments store for an editing session — fetch
 * a real list with pasted creds, edit entries in place, and inject synthetic
 * ("mocked") tournaments alongside them. The work is done client-side via
 * actions exposed by the tournaments feature: `setMockMode`,
 * `seedExternalTournaments`, `addMockTournament`, `updateTournament`,
 * `clearMockTournaments`.
 *
 * Flow:
 *   - `enableMocking()` sets `mockMode = true`. The companion guard in
 *     `setTournaments` and `useTournamentsQuery` then drops real refetches.
 *   - `fetchExternal(sessionKey, playerId)` runs a one-off `fetch()` against
 *     the public tournaments endpoint with QA-pasted creds and seeds the
 *     store via `seedExternalTournaments` (which bypasses the mockMode
 *     early-return that blocks `setTournaments`).
 *   - `createMockTournament(form)` appends a synthetic tournament with id
 *     `qa-mock-<timestamp>`. mockMode is NOT auto-flipped — the UI gates
 *     this behind enableMocking, so the flag is already on by the time we
 *     get here.
 *   - `updateTournamentField(id, patch)` patches a single tournament in
 *     place (no resort).
 *   - `disableMocking()` clears every mock and disables `mockMode` so real
 *     polling resumes; in-place edits to real entries are silently lost on
 *     the next poll, which is expected.
 *
 * Boundary note: this controller imports from `features/campaigns` strictly
 * via the public `index.ts`. The qaPanel build flag is dead-stripped in prod;
 * we therefore must NOT add any imports the other way (campaigns → qaPanel).
 */

import {
    useCampaignsStore,
    isTournamentSource,
    TOURNAMENTS_CAMPAIGN_TYPE,
    TournamentStatuses,
    TournamentFormulas,
    IGameModes,
    CAMPAIGN_LEVELS,
} from '../../campaigns';
import type {
    PrizeGiftSpinConfig,
    TournamentPrizeType,
    ITournament,
    ITournamentResources,
    ITournamentPrize,
    IEligibleGameModes,
    IPlayerScore,
    IParsedTournament,
    ITournamentAPIResponse,
    CampaignLevel,
} from '../../campaigns';
import { useAppStore } from 'store/app/appStore';
import { GET_PRIZE_TEXT_MONEY } from '../../../constants';

/* ── Public types surfaced to the popup ────────────────────────────── */

export interface MockTournamentPrizeForm {
  place: number;
  type: TournamentPrizeType;
  name: string;
  currencyCode: string;
  giftSpinConfig?: PrizeGiftSpinConfig;
}

export interface CreateMockTournamentForm {
  name: string;
  terms: string;
  level: CampaignLevel;
  formula: TournamentFormulas;
  minSpinNumber: number;
  minStake: number;
  baseCurrency: string;
  /** All offsets are in MINUTES relative to "now" at submit time. */
  promoStartOffsetMin: number;
  startOffsetMin: number;
  endOffsetMin: number;
  prizes: MockTournamentPrizeForm[];
  /** When `null`, the auto-sum-from-MONEY-prizes value is used. */
  prizeFundOverride: number | null;
  /** Raw flag — UI passes the BUY_BONUS checkbox state. */
  buyBonusOnly: boolean;
  /**
   * Synthetic player count. Drives BOTH tabs: a `scoreBoard` of this length is
   * generated (Ranking list) and the prize ladder is extended up to this many
   * places (Rewards list). Clamped to 0–500 at build time.
   */
  playerCount: number;
  /**
   * The Ranking tab's "active players" counter (`joinedPlayersCount`). Kept
   * separate from `playerCount` so QA can show a huge number without
   * generating that many scoreboard rows. Clamped to 9 digits at build time.
   */
  activePlayers: number;
}

export interface MockedTournamentRef {
  id: string;
  name: string;
}

/**
 * Compact, transferable view of a tournament used by the QA popup so it can
 * render the editable list without depending on the heavy `IParsedTournament`
 * shape (which carries fully resolved game settings, etc.).
 */
export interface TournamentSummary {
  id: string;
  name: string;
  terms: string;
  status: TournamentStatuses;
  level: CampaignLevel;
  formula: TournamentFormulas;
  minSpinNumber: number;
  minStake: number;
  baseCurrency: string;
  promoStartDate: string;
  promoEndDate: string;
  startDate: string;
  endDate: string;
  /** Optional: the API splits visibleEnd from end. */
  visibleEndDate: string | null;
  prizes: MockTournamentPrizeForm[];
  prizeFund: number;
  /** Drives the Ranking tab's "active players" counter (totalActivePlayers). */
  joinedPlayersCount: number;
  buyBonusOnly: boolean;
  /** True when the id starts with `'qa-mock-'`. */
  isMock: boolean;
}

export interface CampaignsState {
  /** Whether the tournaments store is currently in mock mode. */
  mockMode: boolean;
  /** Light list of tournaments injected via this panel. */
  mocks: MockedTournamentRef[];
  /** Editable summaries of every tournament (real + mock) currently in the store. */
  tournaments: TournamentSummary[];
  /** Best guess at the player's currency at the time of read. */
  defaultBaseCurrency: string;
}

export interface FetchExternalResult {
  ok: boolean;
  /** HTTP status; 0 means network/CORS error, no response received. */
  status?: number;
  message?: string;
  /** Number of tournaments seeded on success. */
  count?: number;
}

/* ── Hard-coded reference data ─────────────────────────────────────── */

/**
 * Hardcoded resource bundle for synthetic tournaments, keyed by the shared
 * `Campaigns*` vocabulary that the asset mapper reads (normalizeCampaignAssets).
 * QA's example server URLs are used as the values so a mock is visually distinct
 * from a real campaign.
 *
 * `FillColor` and `BorderColor` are explicit literals (no fallback path).
 */
const FILL_COLOR = '#111113';
const BORDER_COLOR = '#B16C04';

const CAMPAIGNS_PLATE =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlate1775220633493.webp?v=1775220633493';
const CAMPAIGNS_PLATE_CLOSE =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlateClose1775220637944.webp?v=1775220637944';
const CAMPAIGNS_PLATE_CLOSE_LOGO =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlateCloseLogo1775220646332.webp?v=1775220646332';
const CAMPAIGNS_MULTIVIEW_TRANSPARENT =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloTransparentImage1775220624945.webp?v=1775220624944';
const CAMPAIGNS_SINGLEVIEW_CLOSE =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundClose1775220661731.webp?v=1775220661730';
const CAMPAIGNS_SINGLEVIEW_PORTRAIT =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundPortrait1775220668438.webp?v=1775220668438';
const CAMPAIGNS_SINGLEVIEW_LANDSCAPE =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundLandscape1775220665222.webp?v=1775220665222';
const CAMPAIGNS_MULTIVIEW_DESKTOP =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloTransparentImageDesktop1775220629233.webp?v=1775220629233';
const CAMPAIGNS_MULTIVIEW_PORTRAIT =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloSeparatedBackgroundPortrait1775220621095.webp?v=1775220621095';
const CAMPAIGNS_MULTIVIEW_LANDSCAPE =
  'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloSeparatedBackgroundLandscape1775220617304.webp?v=1775220617304';

function buildMockResources(): ITournamentResources {
    return {
        CampaignsMultiViewSoloSeparatedBackgroundPortrait: CAMPAIGNS_MULTIVIEW_PORTRAIT,
        CampaignsMultiViewSoloSeparatedBackgroundLandscape: CAMPAIGNS_MULTIVIEW_LANDSCAPE,
        CampaignsMultiViewSoloTransparentImage: CAMPAIGNS_MULTIVIEW_TRANSPARENT,
        CampaignsMultiViewSoloTransparentImageDesktop: CAMPAIGNS_MULTIVIEW_DESKTOP,
        CampaignsMultiViewSoloSectionLogo: CAMPAIGNS_PLATE_CLOSE_LOGO,
        CampaignsPlate: CAMPAIGNS_PLATE,
        CampaignsPlateClose: CAMPAIGNS_PLATE_CLOSE,
        CampaignsPlateCloseLogo: CAMPAIGNS_PLATE_CLOSE_LOGO,
        CampaignsSingleViewSoloBackgroundClose: CAMPAIGNS_SINGLEVIEW_CLOSE,
        CampaignsSingleViewSoloBackgroundLandscape: CAMPAIGNS_SINGLEVIEW_LANDSCAPE,
        CampaignsSingleViewSoloBackgroundPortrait: CAMPAIGNS_SINGLEVIEW_PORTRAIT,
        FillColor: FILL_COLOR,
        BorderColor: BORDER_COLOR,
    };
}

/**
 * 50 EGT game keys used as the default participating-games set for any mock
 * tournament. The store's parser silently drops any keys not present in
 * appStore.supportedGames, so an environment with fewer titles still works.
 */
const MOCK_TOURNAMENT_GAMES: string[] = [
  'ARSlot',
  'BCSlot',
  'BCBSlot',
  'FBHRSlot',
  'FVBPSlot',
  'FBRSlot',
  'FVCLHSlot',
  'FVCHTSlot',
  'FVCHSlot',
  'FDCSlot',
  'FDHSlot',
  'FVDRSlot',
  'FVJHSlot',
  'FVLRSlot',
  'FVSHRSlot',
  'FVSHSlot',
  'FVSFSlot',
  'FVSPSlot',
  'FVSRSlot',
  'TADSlot',
  'TBHRSlot',
  'TNBPSlot',
  'TNBRSlot',
  'TNCLHSlot',
  'TCHSlot',
  'TCHBSlot',
  'TCWSlot',
  'TNDCSlot',
  'TNDHSlot',
  'TNDRSlot',
  'TNJHSlot',
  'TNJRSlot',
  'TNLHSlot',
  'TNLRSlot',
  'TSDSlot',
  'TNSHRSlot',
  'TNSHSlot',
  'TNSFSlot',
  'TNSPSlot',
  'TNSRSlot',
  'OBCSlot',
  'OBHSlot',
  'OHBPSlot',
  'OHBRSlot',
  'OHCLHSlot',
  'OHCHTSlot',
  'OHCHSlot',
  'OSFSlot',
  'OSHSlot',
  'OSHSSlot',
];

/* ── Form-default helpers ──────────────────────────────────────────── */

function getDefaultBaseCurrency(): string {
  return useAppStore.getState().balance?.currency ?? 'EUR';
}

// fallow-ignore-next-line unused-export
export function buildDefaultForm(): CreateMockTournamentForm {
  const baseCurrency = getDefaultBaseCurrency();
  return {
    name: 'QA Test Tournament',
    terms: '',
    level: CAMPAIGN_LEVELS.PURPLE,
    formula: TournamentFormulas.WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS,
    minSpinNumber: 25,
    minStake: 25,
    baseCurrency,
    promoStartOffsetMin: -5,
    startOffsetMin: 0,
    endOffsetMin: 240,
    prizes: [
      {
        place: 1,
        type: GET_PRIZE_TEXT_MONEY,
        name: '100',
        currencyCode: baseCurrency,
      },
    ],
    prizeFundOverride: null,
    buyBonusOnly: false,
    playerCount: 25,
    activePlayers: DEFAULT_ACTIVE_PLAYERS,
  };
}

/* ── Tournament builder ────────────────────────────────────────────── */

function emptyPlayerScore(): IPlayerScore {
  return {
    name: '',
    casinoId: 0,
    score: 0,
    position: 0,
    selected: false,
  };
}

/**
 * Plausible display names for synthetic scoreboard rows. Inlined here on
 * purpose — the QA controller must NOT import from `campaigns/mocks` (that's a
 * forbidden cross-feature deep import). Cycled when the player count exceeds
 * the list length.
 */
const MOCK_SCOREBOARD_NAMES: string[] = [
  'NovaRider', 'LuckyLena', 'SpinKing', 'MaxWin77', 'JackpotJoe',
  'ReelQueen', 'BigBetBo', 'CasinoCat', 'SlotSage', 'WildWes',
  'GoldGita', 'AceAnton', 'MegaMara', 'TurboTom', 'FreyaFox',
];

const MOCK_PLAYER_COUNT_MAX = 500;

/**
 * Starting value for the editable "Active players" field on a new mock. Kept
 * independent of the generated `scoreBoard` length so QA lands on a realistic
 * counter regardless of how many synthetic rows they asked for.
 */
const DEFAULT_ACTIVE_PLAYERS = 500;

/** 9 digits — matches the popup's `maxlength` on the Active players inputs. */
const MAX_ACTIVE_PLAYERS = 999999999;

function clampPlayerCount(count: number): number {
  if (!Number.isFinite(count)) return 0;
  return Math.max(0, Math.min(MOCK_PLAYER_COUNT_MAX, Math.floor(count)));
}

function clampActivePlayers(count: number): number {
  if (!Number.isFinite(count)) return 0;
  return Math.max(0, Math.min(MAX_ACTIVE_PLAYERS, Math.floor(count)));
}

/**
 * Synthesize a descending-score scoreboard of `count` rows. One mid-pack row is
 * flagged `selected` and returned as the "current player" so the leaderboard's
 * highlight and RewardsTab's finished-player prize resolve against a plausible
 * position.
 */
function buildMockScoreBoard(count: number): { scoreBoard: IPlayerScore[]; currentPlayer: IPlayerScore } {
  const total = clampPlayerCount(count);
  if (total === 0) {
    return { scoreBoard: [], currentPlayer: emptyPlayerScore() };
  }
  const currentPosition = Math.max(1, Math.round(total / 2));
  const scoreBoard: IPlayerScore[] = [];
  let currentPlayer: IPlayerScore = emptyPlayerScore();
  for (let i = 0; i < total; i++) {
    const position = i + 1;
    const isCurrent = position === currentPosition;
    const entry: IPlayerScore = {
      name: MOCK_SCOREBOARD_NAMES[i % MOCK_SCOREBOARD_NAMES.length],
      casinoId: 100000 + i,
      score: (total - i) * 100,
      position,
      selected: isCurrent,
    };
    scoreBoard.push(entry);
    if (isCurrent) {
      currentPlayer = entry;
    }
  }
  return { scoreBoard, currentPlayer };
}

/**
 * Extend the user's prizes with distinct, descending minor prizes for every
 * place from `maxUserPlace + 1` up to the player count. Distinct values stop
 * RewardsTab's grouping from collapsing them into a single range row, so QA
 * gets a genuinely long, scrollable Rewards list (exercises the scroll-indicator
 * note in the ticket). The user's own top prizes are preserved untouched.
 */
function buildPrizeLadder(
  prizes: MockTournamentPrizeForm[],
  count: number,
  baseCurrency: string,
): ITournamentPrize[] {
  const mapped: ITournamentPrize[] = prizes.map((p) => ({
    name: p.name,
    place: p.place,
    type: p.type,
    currencyCode: p.currencyCode,
    giftSpinConfig: p.giftSpinConfig,
  }));
  const total = clampPlayerCount(count);
  const maxUserPlace = mapped.reduce((max, p) => Math.max(max, p.place), 0);
  for (let place = maxUserPlace + 1; place <= total; place++) {
    mapped.push({
      name: String(total - place + 1),
      place,
      type: GET_PRIZE_TEXT_MONEY,
      currencyCode: baseCurrency,
    });
  }
  return mapped;
}

function autoSumPrizeFund(prizes: MockTournamentPrizeForm[]): number {
  let sum = 0;
  for (const p of prizes) {
    if (p.type !== GET_PRIZE_TEXT_MONEY) continue;
    const n = Number(p.name);
    if (Number.isFinite(n)) sum += n;
  }
  return sum;
}

function offsetIso(now: number, offsetMin: number): string {
  return new Date(now + offsetMin * 60 * 1000).toISOString();
}

/**
 * Extra fields read by some downstream code paths (formula, minSpinNumber,
 * minStake, formulaParams). Although `ITournament` doesn't declare them,
 * the store's parser spreads the original tournament when producing
 * `IParsedTournament`, so attaching them on construction lets the UI use
 * them once parsed. We declare a local intersection type so we can populate
 * them without a cast.
 */
type MockTournamentInput = ITournament & {
  formula: TournamentFormulas;
  minSpinNumber: number;
  minStake: number;
  formulaParams: null;
  /**
   * Not declared on `ITournament`, but the real API splits visibleEnd from
   * end. We default it to equal `endDate` so any downstream code path that
   * reads it (via cast) still gets a sensible value.
   */
  visibleEndDate: string;
  /**
   * Synthetic player count used to build `scoreBoard`/prizes. Carried on the
   * object (like formula/minStake) so it rides the existing preset
   * serialization and can be read back after a preset round-trip.
   */
  playerCount: number;
};

/**
 * Produce a fully-formed `ITournament` ready to be pushed through
 * `addMockTournament`. The store will parse `games: string[]` →
 * `IGameSettings[]` via the same path as a real fetch.
 */
function buildMockTournament(form: CreateMockTournamentForm): MockTournamentInput {
  const id = 'qa-mock-' + Date.now().toString();
  const now = Date.now();
  const restrictions: IEligibleGameModes = {
    // Per spec: when unchecked, eligibleGameModes must be `null` (NOT [])
    // because the API uses null to mean "no restriction".
    eligibleGameModes: form.buyBonusOnly ? [IGameModes.BUY_BONUS] : null,
  };

  // Prize fund reflects the USER's prizes only (not the synthetic minor-prize
  // filler generated from playerCount) so the displayed fund stays meaningful.
  const prizeFund = form.prizeFundOverride ?? autoSumPrizeFund(form.prizes);
  const startDate = offsetIso(now, form.startOffsetMin);
  const endDate = offsetIso(now, form.endOffsetMin);
  const { scoreBoard, currentPlayer } = buildMockScoreBoard(form.playerCount);
  const prizes = buildPrizeLadder(form.prizes, form.playerCount, form.baseCurrency);

  return {
    id,
    name: form.name,
    type: 'tournaments',
    logoUrl: null,
    description: null,
    terms: form.terms,
    // status is no longer user-editable — the lobby derives joinability
    // and lifecycle from start/end dates, so we hard-default ACTIVE.
    status: TournamentStatuses.ACTIVE,
    joined: false,
    promoStartDate: offsetIso(now, form.promoStartOffsetMin),
    // promoEndDate is no longer user-editable; default to startDate per
    // the rule that "promo end == tournament start" for QA mocks.
    promoEndDate: startDate,
    startDate,
    endDate,
    // visibleEndDate is no longer user-editable in the QA panel; default
    // it to endDate so the field is still present on the constructed
    // tournament for any downstream code that reads it via cast.
    visibleEndDate: endDate,
    prizes,
    prizeFund,
    currency: form.baseCurrency,
    level: form.level,
    scoreBoard,
    joinedPlayersCount: clampActivePlayers(form.activePlayers),
    score: currentPlayer,
    games: MOCK_TOURNAMENT_GAMES,
    showWelcomeModal: false,
    autoJoin: false,
    resources: buildMockResources(),
    baseCurrency: form.baseCurrency,
    restrictions,
    formula: form.formula,
    minSpinNumber: form.minSpinNumber,
    minStake: form.minStake,
    formulaParams: null,
    playerCount: clampPlayerCount(form.playerCount),
  };
}

/* ── Controller ───────────────────────────────────────────────────── */

export class CampaignsController {
  private onChange: (() => void) | null = null;
  private storeUnsubscribe: (() => void) | null = null;

    init(onChange: () => void): void {
        this.onChange = onChange;
        // Re-broadcast whenever the tournament list or mockMode flag changes
        // so the popup's mock list stays live (e.g. if QA clears mocks from
        // a different tab, the popup updates).
        this.storeUnsubscribe = useCampaignsStore.subscribe((state, prev) => {
            if (state.mockMode !== prev.mockMode || state.campaigns !== prev.campaigns) {
                this.onChange?.();
            }
        });
    }

    destroy(): void {
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;
        this.onChange = null;
    }

    getState(): CampaignsState {
        const store = useCampaignsStore.getState();
        const tournamentList = store.campaigns.filter(isTournamentSource) as ITournament[];
        const mocks: MockedTournamentRef[] = tournamentList
            .filter((t) => t.id.startsWith('qa-mock-'))
            .map((t) => ({ id: t.id, name: t.name }));
        const tournaments: TournamentSummary[] = tournamentList.map(toSummary);
        return {
            mockMode: store.mockMode,
            mocks,
            tournaments,
            defaultBaseCurrency: getDefaultBaseCurrency(),
        };
    }

    /**
     * Enable mockMode without touching the tournament list. The companion
     * guards in `setTournaments` and `useTournamentsQuery` then drop real
     * refetches; the popup is free to seed/edit/add at will.
     */
    enableMocking(): void {
        useCampaignsStore.getState().setMockMode(true);
    }

    /**
     * Disable mockMode + clear every mock entry. Real polling resumes; the
     * next refetch silently overwrites any in-place edits to real entries —
     * that's expected (the editing session is over).
     */
    disableMocking(): void {
        useCampaignsStore.getState().clearMockCampaigns();
    }

    /**
     * One-shot, QA-driven external fetch using the user-pasted creds. The
     * URL is hard-coded (the public `-ext.egt-ong.com` host) — this does
     * NOT share auth with the lobby's normal tournament fetch path. On
     * success the store is seeded via `seedExternalTournaments`, which is
     * the only door not gated by mockMode.
     */
    async fetchExternal(sessionKey: string, playerId: string): Promise<FetchExternalResult> {
        const url =
            'https://tournaments-ong-qa-ext.egt-ong.com/tournamentservice/api/game-client/tournaments/get-all' +
            `?sessionKey=${encodeURIComponent(sessionKey)}&playerId=${encodeURIComponent(playerId)}`;

        let response: Response;
        try {
            response = await fetch(url, { mode: 'cors' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Network error';
            return { ok: false, status: 0, message };
        }

        if (!response.ok) {
            // Best-effort: try to parse a JSON body for a {code, message}
            // shape. If the body isn't JSON, fall back to statusText.
            let message = response.statusText || 'Request failed';
            try {
                const body = (await response.json()) as { code?: string; message?: string };
                if (body && typeof body.message === 'string' && body.message.length > 0) {
                    message = body.message;
                }
            } catch {
                /* ignore — non-JSON body */
            }
            return { ok: false, status: response.status, message };
        }

        let body: ITournamentAPIResponse;
        try {
            body = (await response.json()) as ITournamentAPIResponse;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Invalid JSON';
            return { ok: false, status: response.status, message };
        }

        const list = Array.isArray(body.tournaments) ? body.tournaments : [];
        const typedList = list.map((t) => ({ ...t, type: TOURNAMENTS_CAMPAIGN_TYPE }));
        useCampaignsStore.getState().seedExternalCampaigns(typedList as never);
        return { ok: true, status: response.status, count: list.length };
    }

    /**
     * Build a tournament from the form payload and inject it. Returns the
     * generated id so the popup can reflect "just created" feedback.
     */
    createMockTournament(form: CreateMockTournamentForm): string {
        const tournament = buildMockTournament(form);
        useCampaignsStore.getState().addMockCampaign(tournament as never);
        return tournament.id;
    }

    /** Remove a single mocked tournament by id. */
    removeMockTournament(id: string): void {
        if (!id.startsWith('qa-mock-')) return;
        useCampaignsStore.getState().removeCampaign(id);
    }

    /**
     * In-place patch of a single tournament (real or mocked). Thin wrapper
     * around the store's `updateCampaignById` action — does NOT resort.
     */
    updateTournamentField(id: string, patch: Partial<ITournament>): void {
        useCampaignsStore.getState().updateCampaignById(id, patch as never);
    }

    /** Clear every mock and resume real polling. */
    clearAll(): void {
        useCampaignsStore.getState().clearMockCampaigns();
    }
}

/* ── Summary helpers ──────────────────────────────────────────────── */

function toSummary(tournament: ITournament | IParsedTournament): TournamentSummary {
  // Both ITournament and IParsedTournament carry the same surface for
  // every field except `games`. The parsed variant also carries
  // formula/minSpinNumber/minStake on the type; the raw variant has them
  // attached at runtime by the controller's `buildMockTournament` even
  // though they're not declared on `ITournament`. We read defensively
  // from a runtime intersection rather than fighting the type system.
  type Runtime = ITournament & {
    formula?: TournamentFormulas;
    minSpinNumber?: number;
    minStake?: number;
    visibleEndDate?: string | null;
  };
  const t = tournament as unknown as Runtime;

  const buyBonusOnly = Boolean(
    t.restrictions &&
    Array.isArray(t.restrictions.eligibleGameModes) &&
    t.restrictions.eligibleGameModes.some((m) => m === IGameModes.BUY_BONUS),
  );

  return {
    id: t.id,
    name: t.name,
    terms: t.terms,
    status: t.status,
    level: t.level,
    formula: t.formula ?? TournamentFormulas.WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS,
    minSpinNumber: t.minSpinNumber ?? 0,
    minStake: t.minStake ?? 0,
    baseCurrency: t.baseCurrency,
    promoStartDate: t.promoStartDate,
    promoEndDate: t.promoEndDate,
    startDate: t.startDate,
    endDate: t.endDate,
    visibleEndDate: t.visibleEndDate ?? null,
    prizes: (t.prizes ?? []).map((p) => ({
      place: p.place,
      type: p.type,
      name: p.name,
      currencyCode: p.currencyCode ?? t.baseCurrency,
      giftSpinConfig: p.giftSpinConfig,
    })),
    prizeFund: t.prizeFund,
    joinedPlayersCount: t.joinedPlayersCount ?? 0,
    buyBonusOnly,
    isMock: t.id.startsWith('qa-mock-'),
  };
}
